/**
 * iter-172 — Parse → Review → Confirm (Section 5).
 *
 * Loaded at /employer/jobs/review/:intakeId. Fetches the structured parse
 * of the intake, pre-fills the PostJob form, and lets the employer edit
 * every structured field before anything publishes. Nothing in here
 * bypasses the pay-band gate — PostJob's own validation is the single
 * source of truth and surfaces the exact California-law error.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PostJob from "@/pages/employer/PostJob";

export default function JobReviewFromIntake() {
    const { intakeId } = useParams();
    const nav = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [initial, setInitial] = useState(null);
    const [initialSkills, setInitialSkills] = useState([]);
    const [initialKnockouts, setInitialKnockouts] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get(`/employer/intakes/${intakeId}/parsed-structured`);
                if (cancelled) return;
                setInitial({
                    title: data.title || "",
                    location: data.location || "",
                    employment_type: data.employment_type || "Full-time",
                    remote_status: data.remote_status || "on_site",
                    industry: "General",
                    experience_level: data.experience_level || "mid",
                    years_experience: String(data.years_experience ?? ""),
                    pay_mode: data.pay_mode || "range",
                    salary_min: data.salary_min != null ? String(data.salary_min) : "",
                    salary_max: data.salary_max != null ? String(data.salary_max) : "",
                    salary_currency: data.salary_currency || "USD",
                    salary_period: data.salary_period || "YEAR",
                    description: data.description || "",
                    benefits: "",
                    apply_url: "",
                });
                // Normalize knockouts: backend returns array of strings; the
                // MatchFieldsSection expects objects with {prompt, type}.
                setInitialSkills(data.required_skills || []);
                setInitialKnockouts(
                    (data.knockout_questions || []).map((q, i) =>
                        typeof q === "string"
                            ? { id: `k_${Date.now().toString(36)}_${i}`, question: q, answer_type: "yes_no" }
                            : q
                    ),
                );
            } catch (err) {
                if (!cancelled) setError(formatApiError(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [intakeId]);

    if (loading) {
        return (
            <div className="min-h-[400px] flex items-center justify-center" data-testid="job-review-loading">
                <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 size={18} className="animate-spin" />
                    <span>Parsing your role…</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-2xl" data-testid="job-review-error">
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 flex items-start gap-3">
                    <AlertTriangle size={18} className="text-rose-500 mt-0.5" />
                    <div>
                        <h2 className="font-heading font-semibold text-foreground">We couldn't parse that role</h2>
                        <p className="text-sm text-muted-foreground mt-1">{error}</p>
                        <button
                            onClick={() => nav("/employer/jobs/new")}
                            className="btn-outline mt-4 text-sm"
                            data-testid="job-review-fallback-btn"
                        >
                            Draft the role manually
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <PostJob
            initial={initial}
            initialSkills={initialSkills}
            initialKnockouts={initialKnockouts}
            fromIntake={intakeId}
        />
    );
}
